import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Banner, EmptyState, ErrorState, Skeleton } from '@/components/ui/Feedback';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { PageHeader } from '@/app/PlaceholderPage';
import { useAuth } from '@/features/auth/AuthContext';
import { databaseApi } from './api';
import { intensiveClassApi } from './intensiveClassApi';
import type {
  BulkImportIntensiveClassRow,
  IndividualIntensiveClass,
  IntensiveClassSession,
  IntensiveClassStatus,
  IntensiveSessionScore,
} from '@lentera/shared';

export function PelitaIntensiveClassAdminPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [notice, setNotice] = useState<{ tone: 'success' | 'danger' | 'info'; message: string } | null>(null);

  // Modals state
  const [formOpen, setFormOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<IndividualIntensiveClass | null>(null);
  const [certModalOpen, setCertModalOpen] = useState(false);
  const [certTarget, setCertTarget] = useState<IndividualIntensiveClass | null>(null);
  const [certFile, setCertFile] = useState<File | null>(null);
  const [certLoading, setCertLoading] = useState(false);
  const [sessionsModalOpen, setSessionsModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<IndividualIntensiveClass | null>(null);

  // Queries
  const { data: participants = [], isLoading, error, refetch } = useQuery({
    queryKey: ['intensive-class', 'participants', { q, status: statusFilter }],
    queryFn: () => intensiveClassApi.listParticipants({ q: q || undefined, status: statusFilter || undefined }),
  });

  const { data: allPelitaResponse } = useQuery({
    queryKey: ['individuals', 'all-options'],
    queryFn: () => databaseApi.listIndividuals({ page: 1, pageSize: 200 }),
  });
  const pelitaOptions = allPelitaResponse?.data ?? [];

  const { data: sessions = [], refetch: refetchSessions } = useQuery({
    queryKey: ['intensive-class', 'sessions'],
    queryFn: () => intensiveClassApi.listSessions(),
  });

  // Calculate statistics
  const total = participants.length;
  const recorded = participants.filter((p) => p.classId).length;
  const avgAttendance =
    recorded > 0
      ? Math.round(
          participants.reduce((acc, p) => acc + (p.classId ? p.attendancePct : 0), 0) / recorded,
        )
      : 0;
  const recordedGains = participants
    .map((p) => p.gainScore)
    .filter((g): g is number => g !== null && g !== undefined);
  const avgGain =
    recordedGains.length > 0
      ? Math.round((recordedGains.reduce((a, b) => a + b, 0) / recordedGains.length) * 10) / 10
      : 0;

  // Download CSV Template
  const handleDownloadTemplate = () => {
    const sessionExample = sessions && sessions.length > 0
      ? sessions.map((s) => s.sessionNumber).join(';')
      : '1;2;3;4;5;6;7;8;9;10';
    const headers = [
      'Kode PELITA',
      'Presensi (%)',
      `Sesi Hadir (contoh: ${sessionExample})`,
      'Pre-Test',
      'Post-Test',
      'Status',
      'Keterangan',
    ];
    const pelitaRows = pelitaOptions.map((p) => [
      p.code,
      '100',
      sessionExample,
      '60',
      '85',
      'lulus',
      'Hadir lengkap seluruh sesi',
    ]);

    const csvContent = [headers.join(','), ...pelitaRows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'template_kelas_intensif_pelita.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (classId: string) => intensiveClassApi.remove(classId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['intensive-class'] });
      setDeleteTarget(null);
      setNotice({ tone: 'success', message: 'Data Kelas Intensif berhasil dihapus.' });
    },
    onError: (err: Error) => {
      setNotice({ tone: 'danger', message: `Gagal menghapus: ${err.message}` });
    },
  });

  // Certificate upload handler
  const handleUploadCert = async () => {
    if (!certTarget || !certFile) return;
    setCertLoading(true);
    try {
      await intensiveClassApi.uploadCertificate(certTarget.individualId, certFile);
      queryClient.invalidateQueries({ queryKey: ['intensive-class'] });
      setCertModalOpen(false);
      setCertTarget(null);
      setCertFile(null);
      setNotice({ tone: 'success', message: 'E-Sertifikat berhasil diunggah.' });
    } catch (err: any) {
      setNotice({ tone: 'danger', message: `Gagal mengunggah sertifikat: ${err.message}` });
    } finally {
      setCertLoading(false);
    }
  };

  // Certificate delete handler
  const handleDeleteCert = async (row: IndividualIntensiveClass) => {
    if (!confirm(`Hapus e-sertifikat milik ${row.individualName}?`)) return;
    try {
      await intensiveClassApi.deleteCertificate(row.individualId);
      queryClient.invalidateQueries({ queryKey: ['intensive-class'] });
      setNotice({ tone: 'success', message: 'E-Sertifikat berhasil dihapus.' });
    } catch (err: any) {
      setNotice({ tone: 'danger', message: `Gagal menghapus: ${err.message}` });
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Kelas Intensif PELITA SLI"
        description="Pencatatan backdate presensi (%), checklist kehadiran sesi, evaluasi pre-test & post-test, e-sertifikat, dan kurikulum tematik."
        action={
          user?.role === 'super_admin' && (
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="secondary" onClick={() => setSessionsModalOpen(true)}>
                ⚙️ Kurikulum Sesi Materi
              </Button>
              <Button variant="secondary" onClick={handleDownloadTemplate}>
                📥 Unduh Template CSV
              </Button>
              <Button variant="secondary" onClick={() => setImportModalOpen(true)}>
                📤 Impor CSV
              </Button>
              <Button
                onClick={() => {
                  setEditingRow(null);
                  setFormOpen(true);
                }}
              >
                + Tambah Capaian
              </Button>
            </div>
          )
        }
      />

      {notice && (
        <div className="relative">
          <Banner tone={notice.tone}>
            {notice.message}
          </Banner>
          <button
            type="button"
            onClick={() => setNotice(null)}
            className="absolute top-3 right-3 text-xs text-ink-muted hover:text-ink font-bold cursor-pointer"
          >
            ✕
          </button>
        </div>
      )}

      {/* Ringkasan Statistik */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="rounded-xl border border-border bg-white p-4 shadow-2xs">
          <div className="text-xs font-medium text-ink-secondary">Total PELITA SLI</div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-2xl font-extrabold text-ink tabular">{total}</span>
            <span className="text-xs text-ink-muted">Individu</span>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-white p-4 shadow-2xs">
          <div className="text-xs font-medium text-ink-secondary">Sudah Tercatat Nilai</div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-2xl font-extrabold text-primary tabular">{recorded}</span>
            <span className="text-xs text-ink-muted">dari {total} ({total > 0 ? Math.round((recorded / total) * 100) : 0}%)</span>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-white p-4 shadow-2xs">
          <div className="text-xs font-medium text-ink-secondary">Rata-rata Presensi</div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-2xl font-extrabold text-ink tabular">{avgAttendance}%</span>
            <span className="text-xs text-ink-muted">Tingkat kehadiran</span>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-white p-4 shadow-2xs">
          <div className="text-xs font-medium text-ink-secondary">Rata-rata Peningkatan (Gain)</div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-2xl font-extrabold text-emerald-600 tabular">
              {avgGain > 0 ? `+${avgGain}` : `${avgGain}`}
            </span>
            <span className="text-xs text-ink-muted">Poin</span>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <Card padded={false}>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 border-b border-border p-4 bg-subtle/40">
          <div className="flex-1 max-w-sm">
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cari kode atau nama PELITA..."
              className="w-full rounded-[var(--radius-input)] border border-border bg-white px-3 py-2 text-xs text-ink focus:border-primary focus:outline-hidden"
            />
          </div>
          <div className="flex items-center gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-[var(--radius-input)] border border-border bg-white px-3 py-2 text-xs text-ink focus:border-primary focus:outline-hidden w-48"
            >
              <option value="">Semua Status</option>
              <option value="lulus">Lulus</option>
              <option value="mengikuti">Mengikuti</option>
              <option value="belum_selesai">Belum Selesai</option>
              <option value="unrecorded">Belum Ada Data</option>
            </select>
          </div>
        </div>

        {/* Tabel Rekapitulasi */}
        {isLoading ? (
          <div className="p-6">
            <Skeleton className="h-64 w-full" />
          </div>
        ) : error ? (
          <div className="p-6">
            <ErrorState message={(error as Error).message} onRetry={() => refetch()} />
          </div>
        ) : participants.length === 0 ? (
          <EmptyState
            icon="🎓"
            title="Tidak ada data ditemukan"
            description="Coba ubah kata kunci pencarian atau filter status."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-subtle text-ink-secondary border-b border-border font-semibold uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3">Kode & Nama PELITA</th>
                  <th className="px-4 py-3">TBM Binaan</th>
                  <th className="px-4 py-3 text-center">Kehadiran Sesi</th>
                  <th className="px-4 py-3 text-center">Pre-Test</th>
                  <th className="px-4 py-3 text-center">Post-Test</th>
                  <th className="px-4 py-3 text-center">Gain</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-center">E-Sertifikat</th>
                  <th className="px-4 py-3 text-right">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {participants.map((r) => {
                  const attendedCount =
                    r.attendedSessions && Array.isArray(r.attendedSessions)
                      ? r.attendedSessions.length
                      : r.classId
                        ? Math.round((r.attendancePct / 100) * sessions.length)
                        : null;

                  return (
                    <tr key={r.individualId} className="hover:bg-subtle/50 transition">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          {r.individualPhotoKey ? (
                            <img
                              src={`/api/v1/files/${r.individualPhotoKey}`}
                              alt={r.individualName}
                              className="size-8 rounded-full object-cover border border-border shrink-0"
                            />
                          ) : (
                            <span className="grid size-8 shrink-0 place-items-center rounded-full bg-primary-soft text-xs font-bold text-primary">
                              {r.individualName?.slice(0, 2).toUpperCase()}
                            </span>
                          )}
                          <div className="min-w-0">
                            <span className="font-semibold text-ink block">{r.individualName}</span>
                            <span className="text-[11px] text-ink-muted tabular">{r.individualCode}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-ink-secondary">
                        {r.tbmName || <span className="text-ink-muted italic">—</span>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {r.classId ? (
                          <div className="inline-flex flex-col items-center">
                            <div className="flex items-center gap-1.5">
                              <span className="font-bold text-ink tabular">{r.attendancePct}%</span>
                              {attendedCount !== null && (
                                <span className="text-[10px] text-ink-muted tabular">
                                  ({attendedCount}/{sessions.length || 10} Sesi)
                                </span>
                              )}
                            </div>
                            <div className="h-1.5 w-16 overflow-hidden rounded-full bg-subtle mt-1">
                              <div
                                className="h-full rounded-full bg-primary"
                                style={{ width: `${Math.min(100, Math.max(0, r.attendancePct))}%` }}
                              />
                            </div>
                          </div>
                        ) : (
                          <span className="text-ink-muted italic">Belum diisi</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center font-medium tabular">
                        {r.preTestScore !== null && r.preTestScore !== undefined ? r.preTestScore : '—'}
                      </td>
                      <td className="px-4 py-3 text-center font-bold text-primary tabular">
                        {r.postTestScore !== null && r.postTestScore !== undefined ? r.postTestScore : '—'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {r.gainScore !== null && r.gainScore !== undefined ? (
                          <div className="flex flex-col items-center">
                            <span
                              className={`inline-block font-extrabold tabular text-[11px] px-2 py-0.5 rounded ${
                                r.gainScore > 0
                                  ? 'bg-emerald-50 text-emerald-700'
                                  : r.gainScore < 0
                                    ? 'bg-rose-50 text-rose-700'
                                    : 'bg-gray-100 text-gray-700'
                              }`}
                            >
                              {r.gainScore > 0 ? `+${r.gainScore}` : `${r.gainScore}`}
                            </span>
                            {r.sessionScores && r.sessionScores.length > 0 && (
                              <span
                                className="text-[10px] text-ink-muted tabular mt-0.5"
                                title="Jumlah evaluasi sesi terekam"
                              >
                                {r.sessionScores.length} sesi
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-ink-muted">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {r.classId ? (
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider ${
                              r.status === 'lulus'
                                ? 'bg-emerald-100 text-emerald-800'
                                : r.status === 'mengikuti'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-amber-100 text-amber-800'
                            }`}
                          >
                            {r.status}
                          </span>
                        ) : (
                          <span className="text-[11px] text-ink-muted italic">Kosong</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {r.certificateKey ? (
                          <div className="inline-flex items-center gap-1.5">
                            <a
                              href={`/api/v1/files/${r.certificateKey}`}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline cursor-pointer"
                            >
                              <span>📜</span>
                              <span>Unduh</span>
                            </a>
                            <button
                              type="button"
                              onClick={() => handleDeleteCert(r)}
                              className="text-ink-muted hover:text-danger text-xs ml-1 cursor-pointer"
                              title="Hapus berkas sertifikat"
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="secondary"
                            className="text-[11px] py-1 px-2 h-auto"
                            onClick={() => {
                              setCertTarget(r);
                              setCertFile(null);
                              setCertModalOpen(true);
                            }}
                          >
                            + Upload
                          </Button>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <Button
                            size="sm"
                            variant="secondary"
                            className="text-[11px] py-1 px-2 h-auto"
                            onClick={() => {
                              setEditingRow(r);
                              setFormOpen(true);
                            }}
                          >
                            {r.classId ? 'Ubah' : 'Input'}
                          </Button>
                          {r.classId && (
                            <Button
                              size="sm"
                              variant="danger"
                              className="text-[11px] py-1 px-2 h-auto"
                              onClick={() => setDeleteTarget(r)}
                            >
                              Hapus
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* MODAL 1: Form Tambah / Edit Capaian Peserta */}
      {formOpen && (
        <ParticipantFormModal
          open={formOpen}
          initial={editingRow}
          allPelita={pelitaOptions}
          sessions={sessions}
          onClose={() => setFormOpen(false)}
          onSuccess={(msg) => {
            queryClient.invalidateQueries({ queryKey: ['intensive-class'] });
            setFormOpen(false);
            setNotice({ tone: 'success', message: msg });
          }}
        />
      )}

      {/* MODAL 2: Upload E-Sertifikat */}
      {certModalOpen && certTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-base font-bold text-ink mb-1">Unggah E-Sertifikat</h3>
            <p className="text-xs text-ink-secondary mb-4">
              Penerima: <strong>{certTarget.individualName}</strong> ({certTarget.individualCode})
            </p>

            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-ink block mb-1.5">
                  Pilih Berkas Sertifikat (PDF, JPG, PNG — Maks. 10 MB)
                </label>
                <input
                  type="file"
                  accept="application/pdf,image/jpeg,image/png,image/webp"
                  onChange={(e) => setCertFile(e.target.files?.[0] || null)}
                  className="w-full text-xs text-ink-secondary file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-primary-soft file:text-primary hover:file:bg-primary/20"
                />
              </div>

              {certFile && (
                <div className="rounded-md bg-subtle p-3 text-xs text-ink">
                  <div>Nama file: <strong>{certFile.name}</strong></div>
                  <div>Ukuran: {(certFile.size / 1024 / 1024).toFixed(2)} MB</div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t border-border">
                <Button
                  variant="secondary"
                  disabled={certLoading}
                  onClick={() => setCertModalOpen(false)}
                >
                  Batal
                </Button>
                <Button
                  disabled={!certFile || certLoading}
                  onClick={handleUploadCert}
                >
                  {certLoading ? 'Mengunggah...' : 'Simpan & Unggah'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 3: Kelola Kurikulum Sesi Aktivitas */}
      {sessionsModalOpen && (
        <SessionsManagerModal
          open={sessionsModalOpen}
          sessions={sessions}
          onClose={() => setSessionsModalOpen(false)}
          onSuccess={() => {
            refetchSessions();
            setSessionsModalOpen(false);
            setNotice({ tone: 'success', message: 'Katalog kurikulum sesi Kelas Intensif berhasil disimpan.' });
          }}
        />
      )}

      {/* MODAL 4: Impor CSV Massal */}
      {importModalOpen && (
        <BulkImportModal
          open={importModalOpen}
          pelitaList={pelitaOptions}
          sessions={sessions}
          onClose={() => setImportModalOpen(false)}
          onSuccess={(res) => {
            queryClient.invalidateQueries({ queryKey: ['intensive-class'] });
            setImportModalOpen(false);
            setNotice({
              tone: 'success',
              message: `Impor berhasil: ${res.imported} data berhasil disimpan dari ${res.total} baris.${
                res.failed > 0 ? ` (${res.failed} baris gagal)` : ''
              }`,
            });
          }}
        />
      )}

      {/* CONFIRM DIALOG: Hapus Capaian */}
      {deleteTarget && (
        <ConfirmDialog
          open={Boolean(deleteTarget)}
          title={`Hapus data Kelas Intensif ${deleteTarget.individualName}?`}
          confirmLabel={deleteMutation.isPending ? 'Menghapus...' : 'Ya, hapus permanen'}
          loading={deleteMutation.isPending}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => deleteMutation.mutate(deleteTarget.classId!)}
        >
          <p className="text-xs text-ink-secondary">
            Nilai kehadiran, pre-test, post-test, checklist sesi, dan berkas e-sertifikat yang telah diunggah akan ikut terhapus.
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
}

/* ── SUBKOMPONEN FORM PESERTA MODAL (DILENGKAPI CHECKLIST 10 SESI) ─────── */

interface ParticipantFormModalProps {
  open: boolean;
  initial: IndividualIntensiveClass | null;
  allPelita: any[];
  sessions: IntensiveClassSession[];
  onClose: () => void;
  onSuccess: (message: string) => void;
}

function ParticipantFormModal({
  initial,
  allPelita,
  sessions,
  onClose,
  onSuccess,
}: ParticipantFormModalProps) {
  const [individualId, setIndividualId] = useState(initial?.individualId || (allPelita[0]?.id ?? ''));

  // Checklist 10 Sesi
  const [attendedSessions, setAttendedSessions] = useState<number[]>(() => {
    if (initial?.attendedSessions && Array.isArray(initial.attendedSessions)) {
      return initial.attendedSessions;
    }
    if (initial?.attendancePct && initial.attendancePct >= 100) {
      return sessions.map((s) => s.sessionNumber);
    }
    // Default to all checked for new entry
    return sessions.map((s) => s.sessionNumber);
  });

  // Nilai Pre/Post Test Per Sesi
  const [sessionScores, setSessionScores] = useState<Record<number, { pre: string; post: string }>>(() => {
    const map: Record<number, { pre: string; post: string }> = {};
    if (initial?.sessionScores && Array.isArray(initial.sessionScores)) {
      for (const s of initial.sessionScores) {
        map[s.sessionNumber] = {
          pre: s.preTest !== null && s.preTest !== undefined ? String(s.preTest) : '',
          post: s.postTest !== null && s.postTest !== undefined ? String(s.postTest) : '',
        };
      }
    }
    return map;
  });

  const [attendancePct, setAttendancePct] = useState<string>(() => {
    if (initial?.attendancePct !== undefined) {
      return String(initial.attendancePct);
    }
    return sessions.length > 0 ? '100' : '0';
  });

  const [preTestScore, setPreTestScore] = useState<string>(
    initial?.preTestScore !== null && initial?.preTestScore !== undefined
      ? String(initial.preTestScore)
      : '',
  );
  const [postTestScore, setPostTestScore] = useState<string>(
    initial?.postTestScore !== null && initial?.postTestScore !== undefined
      ? String(initial.postTestScore)
      : '',
  );
  const [status, setStatus] = useState<IntensiveClassStatus>(initial?.status || 'lulus');
  const [notes, setNotes] = useState(initial?.notes || '');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const preNum = preTestScore !== '' ? parseFloat(preTestScore) : null;
  const postNum = postTestScore !== '' ? parseFloat(postTestScore) : null;
  const gain = preNum !== null && postNum !== null ? Math.round((postNum - preNum) * 100) / 100 : null;

  // Ubah nilai pre/post per sesi dan hitung otomatis rata-rata
  const handleSessionScoreChange = (sessionNum: number, field: 'pre' | 'post', val: string) => {
    setSessionScores((prev) => {
      const next = {
        ...prev,
        [sessionNum]: {
          pre: field === 'pre' ? val : (prev[sessionNum]?.pre ?? ''),
          post: field === 'post' ? val : (prev[sessionNum]?.post ?? ''),
        },
      };

      // Otomatis hitung rata-rata pre & post dari seluruh sesi yang diisi
      const validPres: number[] = [];
      const validPosts: number[] = [];
      for (const k of Object.keys(next)) {
        const numKey = Number(k);
        const p = parseFloat(next[numKey]?.pre ?? '');
        const po = parseFloat(next[numKey]?.post ?? '');
        if (!isNaN(p)) validPres.push(p);
        if (!isNaN(po)) validPosts.push(po);
      }

      if (validPres.length > 0) {
        const avgPre = Math.round((validPres.reduce((a, b) => a + b, 0) / validPres.length) * 10) / 10;
        setPreTestScore(String(avgPre));
      }
      if (validPosts.length > 0) {
        const avgPost = Math.round((validPosts.reduce((a, b) => a + b, 0) / validPosts.length) * 10) / 10;
        setPostTestScore(String(avgPost));
      }

      return next;
    });
  };

  // Toggle checklist sesi
  const toggleSession = (sessionNum: number) => {
    setAttendedSessions((prev) => {
      const next = prev.includes(sessionNum)
        ? prev.filter((n) => n !== sessionNum)
        : [...prev, sessionNum].sort((a, b) => a - b);

      const totalSess = sessions.length || 10;
      const computedPct = Math.round((next.length / totalSess) * 100);
      setAttendancePct(String(computedPct));

      // Otomatis rekomendasikan status: jika >= 80% lulus, lainnya mengikuti
      if (computedPct >= 80) {
        setStatus('lulus');
      } else if (computedPct > 0) {
        setStatus('mengikuti');
      } else {
        setStatus('belum_selesai');
      }

      return next;
    });
  };

  const checkAll = () => {
    const all = sessions.map((s) => s.sessionNumber);
    setAttendedSessions(all);
    setAttendancePct('100');
    setStatus('lulus');
  };

  const uncheckAll = () => {
    setAttendedSessions([]);
    setAttendancePct('0');
    setStatus('belum_selesai');
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const att = parseFloat(attendancePct);
      if (isNaN(att) || att < 0 || att > 100) {
        throw new Error('Persentase kehadiran harus antara 0 s.d. 100.');
      }

      // Kumpulkan session scores yang valid
      const cleanSessionScores: IntensiveSessionScore[] = [];
      for (const s of sessions) {
        const entry = sessionScores[s.sessionNumber];
        const p = entry?.pre !== undefined && entry.pre !== '' ? parseFloat(entry.pre) : null;
        const po = entry?.post !== undefined && entry.post !== '' ? parseFloat(entry.post) : null;
        if ((p !== null && !isNaN(p)) || (po !== null && !isNaN(po))) {
          const g = p !== null && !isNaN(p) && po !== null && !isNaN(po) ? Math.round((po - p) * 100) / 100 : null;
          cleanSessionScores.push({
            sessionNumber: s.sessionNumber,
            preTest: p !== null && !isNaN(p) ? p : null,
            postTest: po !== null && !isNaN(po) ? po : null,
            gain: g,
          });
        }
      }

      const payload = {
        individualId,
        attendancePct: att,
        attendedSessions,
        sessionScores: cleanSessionScores.length > 0 ? cleanSessionScores : null,
        preTestScore: preNum,
        postTestScore: postNum,
        gainScore: gain,
        status,
        notes: notes.trim() || null,
      };

      if (initial?.classId) {
        return intensiveClassApi.update(initial.classId, payload);
      }
      return intensiveClassApi.upsert(payload);
    },
    onSuccess: () => {
      onSuccess(initial?.classId ? 'Capaian berhasil diperbarui.' : 'Capaian baru berhasil disimpan.');
    },
    onError: (err: any) => {
      setErrorMsg(err.message || 'Gagal menyimpan data.');
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col rounded-xl bg-white shadow-2xl overflow-hidden">
        {/* Header Modal */}
        <div className="flex items-center justify-between border-b border-border bg-subtle px-6 py-4">
          <div>
            <h3 className="text-base font-bold text-ink">
              {initial?.classId ? 'Ubah Data Capaian & Kehadiran' : 'Input Capaian & Kehadiran Kelas Intensif'}
            </h3>
            <p className="text-xs text-ink-secondary">
              Centang kehadiran per sesi, masukkan nilai pre-test & post-test per sesi, dan pantau peningkatan kompetensi.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-muted hover:text-ink text-sm font-bold cursor-pointer"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {errorMsg && <Banner tone="danger">{errorMsg}</Banner>}

          {/* Pilih Individu */}
          <div>
            <label className="text-xs font-semibold text-ink block mb-1">Nama PELITA SLI</label>
            {initial?.classId ? (
              <div className="rounded-md border border-border bg-subtle px-3 py-2 text-xs font-semibold text-ink">
                {initial.individualName} ({initial.individualCode})
              </div>
            ) : (
              <select
                value={individualId}
                onChange={(e) => setIndividualId(e.target.value)}
                className="w-full rounded-[var(--radius-input)] border border-border bg-white px-3 py-2 text-xs text-ink focus:border-primary focus:outline-hidden"
              >
                {allPelita.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code} · {p.name} {p.tbmName ? `(${p.tbmName})` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Checklist Rincian Kehadiran & Nilai 10 Sesi */}
          <div className="rounded-xl border border-primary/20 bg-primary-soft/10 p-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-border/80 pb-2.5 mb-3">
              <div>
                <span className="text-xs font-bold text-ink block">
                  Checklist Kehadiran & Evaluasi Per Sesi Kelas Intensif
                </span>
                <span className="text-[11px] text-ink-secondary">
                  Centang kehadiran dan masukkan nilai Pre-Test & Post-Test pada setiap sesi.
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold px-2.5 py-0.5 rounded bg-primary text-white tabular">
                  {attendedSessions.length} / {sessions.length} Hadir ({attendancePct}%)
                </span>
                <button
                  type="button"
                  onClick={checkAll}
                  className="text-[11px] font-semibold text-primary hover:underline cursor-pointer ml-1"
                >
                  Semua
                </button>
                <span className="text-ink-muted text-xs">·</span>
                <button
                  type="button"
                  onClick={uncheckAll}
                  className="text-[11px] font-semibold text-ink-secondary hover:text-danger cursor-pointer"
                >
                  Kosongkan
                </button>
              </div>
            </div>

            <div className="max-h-72 overflow-y-auto rounded-lg border border-border bg-white p-2.5 space-y-2">
              {sessions.map((s) => {
                const isChecked = attendedSessions.includes(s.sessionNumber);
                const sPreStr = sessionScores[s.sessionNumber]?.pre ?? '';
                const sPostStr = sessionScores[s.sessionNumber]?.post ?? '';
                const sPreNum = sPreStr !== '' ? parseFloat(sPreStr) : null;
                const sPostNum = sPostStr !== '' ? parseFloat(sPostStr) : null;
                const sGain =
                  sPreNum !== null && !isNaN(sPreNum) && sPostNum !== null && !isNaN(sPostNum)
                    ? Math.round((sPostNum - sPreNum) * 100) / 100
                    : null;

                return (
                  <div
                    key={s.sessionNumber}
                    className={`p-3 rounded-lg border transition ${
                      isChecked ? 'border-emerald-200 bg-emerald-50/20' : 'border-border/70 bg-subtle/20'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id={`sess-${s.sessionNumber}`}
                        checked={isChecked}
                        onChange={() => toggleSession(s.sessionNumber)}
                        className="mt-1 size-4 rounded border-border text-primary focus:ring-primary cursor-pointer shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <label
                            htmlFor={`sess-${s.sessionNumber}`}
                            className={`text-xs font-semibold cursor-pointer ${
                              isChecked ? 'text-ink' : 'text-ink-secondary'
                            }`}
                          >
                            Sesi #{s.sessionNumber}: {s.topic}
                          </label>
                          <button
                            type="button"
                            onClick={() => toggleSession(s.sessionNumber)}
                            className={`text-[10px] font-bold px-2 py-0.5 rounded shrink-0 cursor-pointer ${
                              isChecked
                                ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                                : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            {isChecked ? 'Hadir ✅' : 'Tidak Hadir'}
                          </button>
                        </div>

                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-ink-muted">
                          <span>🎙️ {s.speaker}</span>
                          {s.date && (
                            <span>
                              📅{' '}
                              {new Date(s.date).toLocaleDateString('id-ID', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                              })}
                            </span>
                          )}
                        </div>

                        {/* Nilai Sesi: Pre-Test & Post-Test */}
                        <div className="mt-2.5 pt-2 border-t border-border/60 flex flex-wrap items-center gap-2.5">
                          <div className="flex items-center gap-1.5">
                            <span className="text-[11px] font-medium text-ink-secondary">Pre-Test:</span>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.5"
                              placeholder="0-100"
                              value={sPreStr}
                              onChange={(e) => handleSessionScoreChange(s.sessionNumber, 'pre', e.target.value)}
                              className="w-16 rounded border border-border bg-white px-2 py-1 text-xs text-ink focus:border-primary focus:outline-hidden tabular"
                            />
                          </div>

                          <span className="text-ink-muted text-xs">➔</span>

                          <div className="flex items-center gap-1.5">
                            <span className="text-[11px] font-medium text-primary">Post-Test:</span>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.5"
                              placeholder="0-100"
                              value={sPostStr}
                              onChange={(e) => handleSessionScoreChange(s.sessionNumber, 'post', e.target.value)}
                              className="w-16 rounded border border-border bg-white px-2 py-1 text-xs text-ink focus:border-primary focus:outline-hidden tabular font-semibold text-primary"
                            />
                          </div>

                          <div className="flex items-center gap-1 ml-auto">
                            <span className="text-[10px] text-ink-muted">Gain:</span>
                            <span
                              className={`text-[11px] font-bold px-1.5 py-0.5 rounded tabular ${
                                sGain !== null && sGain > 0
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : sGain !== null && sGain < 0
                                    ? 'bg-rose-100 text-rose-800'
                                    : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {sGain !== null ? (sGain > 0 ? `+${sGain}` : `${sGain}`) : '—'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Kehadiran (%) & Status */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-ink block mb-1">
                Tingkat Kehadiran (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={attendancePct}
                onChange={(e) => setAttendancePct(e.target.value)}
                placeholder="Contoh: 100 atau 87.5"
                className="w-full rounded-[var(--radius-input)] border border-border bg-white px-3 py-2 text-xs text-ink focus:border-primary focus:outline-hidden"
              />
              <span className="text-[10px] text-ink-muted mt-1 block">
                Otomatis dihitung dari checklist sesi (dapat disesuaikan)
              </span>
            </div>

            <div>
              <label className="text-xs font-semibold text-ink block mb-1">Status Kelulusan</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as IntensiveClassStatus)}
                className="w-full rounded-[var(--radius-input)] border border-border bg-white px-3 py-2 text-xs text-ink focus:border-primary focus:outline-hidden"
              >
                <option value="lulus">Lulus</option>
                <option value="mengikuti">Mengikuti</option>
                <option value="belum_selesai">Belum Selesai</option>
              </select>
            </div>
          </div>

          {/* Nilai Pre-Test & Post-Test Keseluruhan */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold text-ink">Nilai Pre-Test (Rata-rata)</label>
                <span className="text-[10px] text-ink-muted">Skala 0 - 100</span>
              </div>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={preTestScore}
                onChange={(e) => setPreTestScore(e.target.value)}
                placeholder="Rata-rata Pre-Test"
                className="w-full rounded-[var(--radius-input)] border border-border bg-white px-3 py-2 text-xs text-ink focus:border-primary focus:outline-hidden"
              />
              <span className="text-[10px] text-ink-muted mt-1 block">
                Otomatis dihitung dari rata-rata nilai sesi
              </span>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold text-ink">Nilai Post-Test (Rata-rata)</label>
                <span className="text-[10px] text-ink-muted">Skala 0 - 100</span>
              </div>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={postTestScore}
                onChange={(e) => setPostTestScore(e.target.value)}
                placeholder="Rata-rata Post-Test"
                className="w-full rounded-[var(--radius-input)] border border-border bg-white px-3 py-2 text-xs text-ink focus:border-primary focus:outline-hidden font-semibold text-primary"
              />
              <span className="text-[10px] text-ink-muted mt-1 block">
                Otomatis dihitung dari rata-rata nilai sesi
              </span>
            </div>
          </div>

          {/* Auto Gain Indicator Keseluruhan */}
          <div className="rounded-lg border border-border bg-subtle p-3 flex items-center justify-between text-xs">
            <span className="font-medium text-ink-secondary">Peningkatan Kompetensi Total (Gain Score):</span>
            <span
              className={`font-extrabold text-sm tabular ${
                gain !== null && gain > 0
                  ? 'text-emerald-600'
                  : gain !== null && gain < 0
                    ? 'text-rose-600'
                    : 'text-ink'
              }`}
            >
              {gain !== null ? (gain > 0 ? `+${gain}` : `${gain}`) : '—'}
            </span>
          </div>

          {/* Catatan Fasilitator */}
          <div>
            <label className="text-xs font-semibold text-ink block mb-1">Catatan Fasilitator</label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Catatan keaktifan atau rekomendasi pendampingan..."
              className="w-full rounded-[var(--radius-input)] border border-border bg-white px-3 py-2 text-xs text-ink focus:border-primary focus:outline-hidden"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-border bg-subtle px-6 py-4">
          <Button variant="secondary" onClick={onClose} disabled={saveMutation.isPending}>
            Batal
          </Button>
          <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Menyimpan...' : 'Simpan Data'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ── SUBKOMPONEN KELOLA KURIKULUM & TANGGAL SESI MODAL (FLEKSIBEL TAMBAH/HAPUS) ──── */

interface SessionsManagerModalProps {
  open: boolean;
  sessions: IntensiveClassSession[];
  onClose: () => void;
  onSuccess: () => void;
}

function SessionsManagerModal({ sessions: initialSessions, onClose, onSuccess }: SessionsManagerModalProps) {
  const [items, setItems] = useState<IntensiveClassSession[]>(() => {
    if (initialSessions && initialSessions.length > 0) {
      return [...initialSessions].sort((a, b) => a.sessionNumber - b.sessionNumber);
    }
    const result: IntensiveClassSession[] = [];
    for (let i = 1; i <= 10; i++) {
      result.push({
        id: `ics-${i}`,
        sessionNumber: i,
        topic: `Materi Sesi ${i}`,
        speaker: 'Narasumber SLI',
        description: '',
        date: '',
      });
    }
    return result;
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFieldChange = (index: number, field: keyof IntensiveClassSession, val: string) => {
    setItems((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index]!, [field]: val };
      return copy;
    });
  };

  const handleAddSession = () => {
    setError(null);
    setItems((prev) => {
      const nextNum = prev.length + 1;
      return [
        ...prev,
        {
          id: `ics-${Date.now()}-${nextNum}`,
          sessionNumber: nextNum,
          topic: `Materi Sesi ${nextNum}`,
          speaker: 'Narasumber SLI',
          description: '',
          date: '',
        },
      ];
    });
  };

  const handleRemoveSession = (index: number) => {
    if (items.length <= 1) {
      setError('Minimal harus ada 1 sesi materi.');
      return;
    }
    setError(null);
    setItems((prev) => {
      const filtered = prev.filter((_, i) => i !== index);
      return filtered.map((item, idx) => ({
        ...item,
        sessionNumber: idx + 1,
      }));
    });
  };

  const handleSave = async () => {
    if (items.length === 0) {
      setError('Minimal harus ada 1 sesi materi.');
      return;
    }
    for (const item of items) {
      if (!item.topic.trim()) {
        setError(`Judul topik untuk Sesi #${item.sessionNumber} tidak boleh kosong.`);
        return;
      }
      if (!item.speaker.trim()) {
        setError(`Nama narasumber untuk Sesi #${item.sessionNumber} tidak boleh kosong.`);
        return;
      }
    }

    setSaving(true);
    setError(null);
    try {
      await intensiveClassApi.updateSessions(
        items.map((it, idx) => ({
          sessionNumber: idx + 1,
          topic: it.topic.trim(),
          speaker: it.speaker.trim(),
          description: it.description?.trim() || null,
          date: it.date?.trim() || null,
        })),
      );
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Gagal menyimpan katalog sesi.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="flex max-h-[92vh] w-full max-w-4xl flex-col rounded-xl bg-white shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between border-b border-border bg-subtle px-6 py-4">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold text-ink">Kelola Kurikulum & Tanggal Sesi Materi</h3>
              <span className="rounded-full bg-primary/10 text-primary font-bold text-xs px-2.5 py-0.5">
                {items.length} Sesi
              </span>
            </div>
            <p className="text-xs text-ink-secondary mt-0.5">
              Atur judul materi, narasumber, tanggal pelaksanaan, serta tambah atau hapus sesi secara fleksibel.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleAddSession}
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-primary bg-primary-soft hover:bg-primary/20 rounded-md transition-colors cursor-pointer"
            >
              <span>➕ Tambah Sesi</span>
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-ink-muted hover:text-ink text-sm font-bold cursor-pointer p-1"
            >
              ✕
            </button>
          </div>
        </div>

        {error && (
          <div className="p-4 border-b border-border">
            <Banner tone="danger">{error}</Banner>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-6 space-y-3.5">
          {items.map((item, idx) => (
            <div
              key={item.id || idx}
              className="flex items-start gap-3 rounded-lg border border-border bg-subtle/30 p-3.5 hover:border-border-strong transition-colors"
            >
              <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-primary font-bold text-xs text-white mt-1 shadow-xs">
                {item.sessionNumber}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2.5 flex-1">
                <div className="sm:col-span-5">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-ink-muted block mb-1">
                    Tema Materi Sesi #{item.sessionNumber}
                  </label>
                  <input
                    type="text"
                    value={item.topic}
                    onChange={(e) => handleFieldChange(idx, 'topic', e.target.value)}
                    placeholder="Contoh: Pengelolaan Administrasi TBM"
                    className="w-full rounded border border-border bg-white px-2.5 py-1.5 text-xs text-ink focus:border-primary focus:outline-hidden"
                  />
                </div>
                <div className="sm:col-span-3">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-ink-muted block mb-1">
                    Narasumber
                  </label>
                  <input
                    type="text"
                    value={item.speaker}
                    onChange={(e) => handleFieldChange(idx, 'speaker', e.target.value)}
                    placeholder="Nama narasumber"
                    className="w-full rounded border border-border bg-white px-2.5 py-1.5 text-xs text-ink focus:border-primary focus:outline-hidden"
                  />
                </div>
                <div className="sm:col-span-3">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-ink-muted block mb-1">
                    Tanggal Pelaksanaan
                  </label>
                  <input
                    type="date"
                    value={item.date ? item.date.split('T')[0] : ''}
                    onChange={(e) => handleFieldChange(idx, 'date', e.target.value)}
                    className="w-full rounded border border-border bg-white px-2.5 py-1.5 text-xs text-ink focus:border-primary focus:outline-hidden"
                  />
                </div>
                <div className="sm:col-span-1 flex items-end justify-center pb-0.5">
                  <button
                    type="button"
                    onClick={() => handleRemoveSession(idx)}
                    disabled={items.length <= 1}
                    title={items.length <= 1 ? 'Minimal 1 sesi' : `Hapus Sesi #${item.sessionNumber}`}
                    className={`p-1.5 rounded text-xs transition-colors cursor-pointer ${
                      items.length <= 1
                        ? 'opacity-30 cursor-not-allowed text-ink-muted'
                        : 'text-red-600 hover:bg-red-50 hover:text-red-700'
                    }`}
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          ))}

          {/* Tombol Tambah Sesi di bawah list */}
          <div className="pt-2">
            <button
              type="button"
              onClick={handleAddSession}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg border-2 border-dashed border-primary/40 bg-primary-soft/30 hover:bg-primary-soft/60 text-primary font-semibold text-xs transition-colors cursor-pointer"
            >
              <span>➕</span>
              <span>Tambah Sesi Materi Baru (#{items.length + 1})</span>
            </button>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-border bg-subtle px-6 py-4">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Batal
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Menyimpan...' : `Simpan Kurikulum Sesi (${items.length} Sesi)`}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ── SUBKOMPONEN IMPOR CSV MODAL ────────────────────────────────────────── */

interface BulkImportModalProps {
  open: boolean;
  pelitaList?: any[];
  sessions?: IntensiveClassSession[];
  onClose: () => void;
  onSuccess: (res: { total: number; imported: number; failed: number }) => void;
}

function BulkImportModal({
  open: _open,
  pelitaList = [],
  sessions = [],
  onClose,
  onSuccess,
}: BulkImportModalProps) {
  const [parsedRows, setParsedRows] = useState<BulkImportIntensiveClassRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  // Helper untuk membelah baris CSV dengan pemisah (, atau ;) termasuk mendukung nilai bertanda kutip
  const splitCsvLine = (line: string, delimiter: string): string[] => {
    const parts: string[] = [];
    let cur = '';
    let inQuote = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') {
        if (inQuote && line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuote = !inQuote;
        }
      } else if (c === delimiter && !inQuote) {
        parts.push(cur.trim());
        cur = '';
      } else {
        cur += c;
      }
    }
    parts.push(cur.trim());
    return parts;
  };

  const cleanVal = (val?: string): string => {
    if (!val) return '';
    let v = val.trim();
    if (v.startsWith('"') && v.endsWith('"')) {
      v = v.slice(1, -1).replace(/""/g, '"');
    }
    return v.trim();
  };

  const parseNum = (val?: string): number | null => {
    if (!val) return null;
    const cleaned = cleanVal(val).replace(',', '.');
    if (!cleaned) return null;
    const n = parseFloat(cleaned);
    return !isNaN(n) ? n : null;
  };

  const handleFileUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = String(e.target?.result ?? '');
      parseCSV(text);
    };
    reader.readAsText(file);
  };

  const parseCSV = (text: string) => {
    setParseError(null);
    try {
      const rawLines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
      if (rawLines.length < 2 || !rawLines[0]) {
        throw new Error('Berkas CSV minimal harus memiliki 1 baris tajuk (header) dan 1 baris data.');
      }

      // Deteksi pemisah (, atau ;)
      const firstLine = rawLines[0];
      const delimiter = firstLine.includes(';') ? ';' : ',';
      const rawHeaders = splitCsvLine(firstLine, delimiter).map((h) => cleanVal(h).toLowerCase());

      const codeIdx = rawHeaders.findIndex((h) => h.includes('kode'));
      const nameIdx = rawHeaders.findIndex((h) => h.includes('nama'));
      const attIdx = rawHeaders.findIndex((h) => h === 'presensi' || h.includes('kehadiran'));
      const sessIdx = rawHeaders.findIndex((h) => h.includes('sesi hadir'));
      const statusIdx = rawHeaders.findIndex((h) => h.includes('status'));
      const notesIdx = rawHeaders.findIndex((h) => h.includes('ket') || h.includes('catatan'));

      // Kolom global Pre / Post jika tidak menggunakan per sesi
      const overallPreIdx = rawHeaders.findIndex(
        (h) => (h === 'pre' || h === 'pre-test' || h === 'pre_test' || h === 'pretest' || h.includes('rata pre')) &&
          !h.match(/\d|s\d|sesi/i),
      );
      const overallPostIdx = rawHeaders.findIndex(
        (h) => (h === 'post' || h === 'post-test' || h === 'post_test' || h === 'posttest' || h.includes('rata post')) &&
          !h.match(/\d|s\d|sesi/i),
      );

      if (codeIdx === -1) {
        throw new Error('Kolom "Kode PELITA" wajib ada di berkas CSV.');
      }

      const rows: BulkImportIntensiveClassRow[] = [];

      for (let i = 1; i < rawLines.length; i++) {
        const line = rawLines[i];
        if (!line) continue;
        const parts = splitCsvLine(line, delimiter).map((p) => cleanVal(p));
        const code = parts[codeIdx];
        if (!code) continue;

        const name = nameIdx !== -1 ? parts[nameIdx] || null : null;
        const notes = notesIdx !== -1 ? parts[notesIdx] || null : null;
        const rawStatus = statusIdx !== -1 ? parts[statusIdx]?.toLowerCase() : undefined;

        // Parsing kehadiran & evaluasi nilai 10 sesi secara terinci
        const attendedSessions: number[] = [];
        const rowSessionScores: IntensiveSessionScore[] = [];

        for (let sNum = 1; sNum <= 10; sNum++) {
          // Cari kolom kehadiran sesi
          const hCol = rawHeaders.findIndex(
            (h) =>
              h === `hadir_s${sNum}` ||
              h === `s${sNum}_hadir` ||
              h === `sesi_${sNum}_hadir` ||
              h === `sesi ${sNum} hadir` ||
              h === `hadir sesi ${sNum}` ||
              h === `hadir s${sNum}` ||
              h === `h_${sNum}` ||
              h === `h${sNum}`,
          );

          let isAttended = false;
          if (hCol !== -1 && parts[hCol] !== undefined && parts[hCol] !== '') {
            const hVal = parts[hCol]!.toLowerCase();
            if (['1', 'ya', 'y', 'hadir', 'h', 'true', 'yes', 'v', 'x', '✓', '✔'].includes(hVal)) {
              isAttended = true;
            }
          }

          // Cari kolom Pre-Test sesi
          const pCol = rawHeaders.findIndex(
            (h) =>
              h === `pre_s${sNum}` ||
              h === `s${sNum}_pre` ||
              h === `pre_${sNum}` ||
              h === `pre${sNum}` ||
              h === `pre-test ${sNum}` ||
              h === `pre-test s${sNum}` ||
              h === `pre test ${sNum}` ||
              h === `pre sesi ${sNum}` ||
              h === `pre_sesi_${sNum}`,
          );

          // Cari kolom Post-Test sesi
          const poCol = rawHeaders.findIndex(
            (h) =>
              h === `post_s${sNum}` ||
              h === `s${sNum}_post` ||
              h === `post_${sNum}` ||
              h === `post${sNum}` ||
              h === `post-test ${sNum}` ||
              h === `post-test s${sNum}` ||
              h === `post test ${sNum}` ||
              h === `post sesi ${sNum}` ||
              h === `post_sesi_${sNum}`,
          );

          const pVal = pCol !== -1 ? parseNum(parts[pCol]) : null;
          const poVal = poCol !== -1 ? parseNum(parts[poCol]) : null;

          if (pVal !== null || poVal !== null) {
            const gVal =
              pVal !== null && poVal !== null ? Math.round((poVal - pVal) * 100) / 100 : null;
            rowSessionScores.push({
              sessionNumber: sNum,
              preTest: pVal,
              postTest: poVal,
              gain: gVal,
            });
            // Jika kolom kehadiran tidak dibuat tetapi peserta memiliki nilai sesi, anggap hadir
            if (hCol === -1) {
              isAttended = true;
            }
          }

          if (isAttended) {
            attendedSessions.push(sNum);
          }
        }

        // Jika kehadiran per sesi tidak ada kolomnya di CSV, cek kolom sesi hadir atau presensi global
        if (attendedSessions.length === 0) {
          if (sessIdx !== -1 && parts[sessIdx]) {
            const rawSess = parts[sessIdx]!;
            const nums = rawSess
              .split(/[;,-]/)
              .map((s) => parseInt(s.trim(), 10))
              .filter((n) => !isNaN(n) && n >= 1 && n <= 10);
            if (nums.length > 0) {
              attendedSessions.push(...new Set(nums));
              attendedSessions.sort((a, b) => a - b);
            }
          }
        }

        // Tentukan persentase kehadiran
        let finalAttPct = 100;
        if (attendedSessions.length > 0) {
          finalAttPct = Math.round((attendedSessions.length / 10) * 100);
        } else if (attIdx !== -1 && parts[attIdx]) {
          const rawAtt = parseNum(parts[attIdx]);
          if (rawAtt !== null) {
            finalAttPct = Math.min(100, Math.max(0, rawAtt));
            if (finalAttPct >= 100) {
              attendedSessions.push(1, 2, 3, 4, 5, 6, 7, 8, 9, 10);
            }
          }
        } else {
          // Default ke 100 jika tidak dispesifikasi
          finalAttPct = 100;
          attendedSessions.push(1, 2, 3, 4, 5, 6, 7, 8, 9, 10);
        }

        // Hitung rata-rata nilai pre & post
        let finalPre = overallPreIdx !== -1 ? parseNum(parts[overallPreIdx]) : null;
        let finalPost = overallPostIdx !== -1 ? parseNum(parts[overallPostIdx]) : null;

        if (rowSessionScores.length > 0) {
          const validP = rowSessionScores.map((s) => s.preTest).filter((v) => v !== null) as number[];
          if (validP.length > 0) {
            finalPre = Math.round((validP.reduce((a, b) => a + b, 0) / validP.length) * 10) / 10;
          }
          const validPo = rowSessionScores.map((s) => s.postTest).filter((v) => v !== null) as number[];
          if (validPo.length > 0) {
            finalPost = Math.round((validPo.reduce((a, b) => a + b, 0) / validPo.length) * 10) / 10;
          }
        }

        let status: IntensiveClassStatus = 'mengikuti';
        if (rawStatus === 'lulus' || rawStatus === 'mengikuti' || rawStatus === 'belum_selesai') {
          status = rawStatus;
        } else if (finalAttPct >= 80) {
          status = 'lulus';
        }

        rows.push({
          code,
          name,
          attendancePct: finalAttPct,
          attendedSessions: attendedSessions.length > 0 ? attendedSessions : null,
          sessionScores: rowSessionScores.length > 0 ? rowSessionScores : null,
          preTestScore: finalPre,
          postTestScore: finalPost,
          status,
          notes,
        });
      }

      setParsedRows(rows);
    } catch (err: any) {
      setParseError(err.message || 'Gagal membaca format CSV.');
      setParsedRows([]);
    }
  };

  const handleImport = async () => {
    if (parsedRows.length === 0) return;
    setUploading(true);
    setParseError(null);
    try {
      const res = await intensiveClassApi.importBulk(parsedRows);
      onSuccess(res);
    } catch (err: any) {
      setParseError(err.message || 'Gagal mengimpor data.');
    } finally {
      setUploading(false);
    }
  };

  // Unduh Template Lengkap 10 Sesi (Pra-isi Nama & Kode PELITA)
  const downloadDetailedTemplate = () => {
    // Bangun tajuk kolom lengkap
    const headers = [
      'Kode PELITA',
      'Nama PELITA',
      'TBM Binaan',
      'Hadir_S1', 'Pre_S1', 'Post_S1',
      'Hadir_S2', 'Pre_S2', 'Post_S2',
      'Hadir_S3', 'Pre_S3', 'Post_S3',
      'Hadir_S4', 'Pre_S4', 'Post_S4',
      'Hadir_S5', 'Pre_S5', 'Post_S5',
      'Hadir_S6', 'Pre_S6', 'Post_S6',
      'Hadir_S7', 'Pre_S7', 'Post_S7',
      'Hadir_S8', 'Pre_S8', 'Post_S8',
      'Hadir_S9', 'Pre_S9', 'Post_S9',
      'Hadir_S10', 'Pre_S10', 'Post_S10',
      'Status',
      'Catatan',
    ];

    let csvRows = [headers.join(',')];

    if (pelitaList && pelitaList.length > 0) {
      for (const p of pelitaList) {
        const esc = (val: string) => `"${(val || '').replace(/"/g, '""')}"`;
        const row = [
          esc(p.code),
          esc(p.name),
          esc(p.tbmName || ''),
          // Default Hadir=1 pada sesi 1 s.d. 10, nilai pre/post kosong siap diisi
          1, '', '', // S1
          1, '', '', // S2
          1, '', '', // S3
          1, '', '', // S4
          1, '', '', // S5
          1, '', '', // S6
          1, '', '', // S7
          1, '', '', // S8
          1, '', '', // S9
          1, '', '', // S10
          'lulus',
          '',
        ];
        csvRows.push(row.join(','));
      }
    } else {
      // Sampel dummy jika daftar individu belum termuat
      csvRows.push(
        'PELITA-001,"Sry Eka Handayani","TBM Lentera Sahabat",1,70,85,1,65,80,1,75,90,1,80,95,1,70,85,1,65,80,1,75,90,1,80,85,1,70,90,1,85,95,lulus,"Sangat aktif"',
      );
      csvRows.push(
        'PELITA-002,"Ahmad Fauzi","TBM Cerdas Mandiri",1,60,75,1,65,80,1,70,85,0,,,1,75,85,1,60,80,1,70,80,1,65,75,1,70,85,1,80,90,lulus,"Izin di sesi 4"',
      );
    }

    const csvContent = 'data:text/csv;charset=utf-8,\uFEFF' + encodeURIComponent(csvRows.join('\n'));
    const link = document.createElement('a');
    link.setAttribute('href', csvContent);
    link.setAttribute('download', 'template_kelas_intensif_10_sesi_pelita.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Unduh Template Sederhana (Agregat / Ringkas)
  const downloadSimpleTemplate = () => {
    const headers = ['Kode PELITA', 'Nama PELITA', 'Presensi (%)', 'Sesi Hadir', 'Pre-Test', 'Post-Test', 'Status', 'Catatan'];
    let csvRows = [headers.join(',')];

    if (pelitaList && pelitaList.length > 0) {
      for (const p of pelitaList) {
        const esc = (val: string) => `"${(val || '').replace(/"/g, '""')}"`;
        csvRows.push(`${esc(p.code)},${esc(p.name)},100,"1;2;3;4;5;6;7;8;9;10",,,lulus,`);
      }
    } else {
      csvRows.push('PELITA-001,"Sry Eka Handayani",100,"1;2;3;4;5;6;7;8;9;10",75,90,lulus,"Sangat aktif"');
      csvRows.push('PELITA-002,"Ahmad Fauzi",90,"1;2;3;4;5;6;7;8;9",65,85,lulus,"Aktif"');
    }

    const csvContent = 'data:text/csv;charset=utf-8,\uFEFF' + encodeURIComponent(csvRows.join('\n'));
    const link = document.createElement('a');
    link.setAttribute('href', csvContent);
    link.setAttribute('download', 'template_kelas_intensif_ringkas.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="flex max-h-[92vh] w-full max-w-4xl flex-col rounded-xl bg-white shadow-2xl overflow-hidden">
        {/* Header Modal */}
        <div className="flex items-center justify-between border-b border-border bg-subtle px-6 py-4">
          <div>
            <h3 className="text-base font-bold text-ink">Impor Massal Capaian Kelas Intensif</h3>
            <p className="text-xs text-ink-secondary">
              Unggah file CSV dengan rincian kehadiran dan nilai evaluasi Pre-Test & Post-Test per sesi.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-muted hover:text-ink text-sm font-bold cursor-pointer"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {parseError && <Banner tone="danger">{parseError}</Banner>}

          {/* Opsi Unduh Template CSV */}
          <div className="rounded-xl border border-primary/20 bg-gradient-to-r from-primary-soft/30 via-white to-primary-soft/10 p-4 shadow-2xs">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <span className="text-xs font-bold text-ink flex items-center gap-1.5">
                  <span>📥</span> Unduh Format Template CSV Standar
                </span>
                <p className="text-[11px] text-ink-secondary mt-0.5">
                  Template sudah otomatis memuat daftar anggota PELITA SLI yang terdaftar di database sistem.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={downloadDetailedTemplate}
                  className="px-3 py-2 rounded-lg bg-primary text-white font-semibold text-xs shadow-xs hover:bg-primary-hover transition cursor-pointer flex items-center gap-1.5"
                >
                  <span>📊</span>
                  <span>Template Rinci 10 Sesi (Rekomendasi)</span>
                </button>
                <button
                  type="button"
                  onClick={downloadSimpleTemplate}
                  className="px-3 py-2 rounded-lg border border-border bg-white text-ink-secondary hover:text-ink hover:bg-subtle font-semibold text-xs transition cursor-pointer"
                >
                  Template Ringkas
                </button>
              </div>
            </div>

            {/* Toggle Panduan Skema Kolom */}
            <div className="mt-3 pt-3 border-t border-border/60">
              <button
                type="button"
                onClick={() => setShowGuide((v) => !v)}
                className="text-[11px] font-semibold text-primary hover:underline cursor-pointer flex items-center gap-1"
              >
                <span>{showGuide ? '▼ Sembunyikan Panduan Kolom' : '► Lihat Panduan Skema Kolom'}</span>
              </button>

              {showGuide && (
                <div className="mt-2.5 rounded-lg bg-white p-3 border border-border text-[11px] space-y-2">
                  <div className="font-semibold text-ink">Struktur Kolom Template Rinci 10 Sesi:</div>
                  <ul className="list-disc pl-4 space-y-1 text-ink-secondary">
                    <li>
                      <strong className="text-ink">Kode PELITA:</strong> Wajib (contoh: PELITA-001). Digunakan sebagai kunci pengenal peserta.
                    </li>
                    <li>
                      <strong className="text-ink">Nama PELITA & TBM Binaan:</strong> Kolom referensi agar pengisian baris tidak tertukar.
                    </li>
                    <li>
                      <strong className="text-ink">Hadir_S1 s.d. Hadir_S10:</strong> Kehadiran per sesi. Isi <code>1</code> jika hadir, atau <code>0</code> jika absen.
                    </li>
                    <li>
                      <strong className="text-ink">Pre_S1 s.d. Pre_S10:</strong> Nilai Pre-Test sesi 1 s.d. 10 (skala 0 - 100, bisa gunakan titik atau koma desimal).
                    </li>
                    <li>
                      <strong className="text-ink">Post_S1 s.d. Post_S10:</strong> Nilai Post-Test sesi 1 s.d. 10 (skala 0 - 100).
                    </li>
                    <li>
                      <strong className="text-ink">Status & Catatan:</strong> Opsional (status otomatis diisi &apos;lulus&apos; jika persentase kehadiran &gt;= 80%).
                    </li>
                  </ul>
                  {sessions.length > 0 && (
                    <div className="pt-2 border-t border-border/60 text-[10px] text-ink-muted">
                      <span className="font-semibold text-ink block mb-1">Daftar Tema 10 Sesi Kelas Intensif:</span>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                        {sessions.map((s) => (
                          <div key={s.sessionNumber} className="truncate">
                            <strong>Sesi #{s.sessionNumber}:</strong> {s.topic}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Area Unggah Berkas */}
          <div className="border-2 border-dashed border-border rounded-xl p-6 text-center hover:border-primary/50 transition bg-subtle/30">
            <span className="text-3xl block mb-2">📁</span>
            <p className="text-xs font-semibold text-ink mb-1">
              Pilih atau seret berkas CSV yang telah diisi ke sini
            </p>
            <p className="text-[11px] text-ink-muted mb-3">
              Mendukung format pemisah koma (,) maupun titik-koma (;) standar Microsoft Excel.
            </p>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0])}
              className="text-xs text-ink-secondary file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-primary-soft file:text-primary hover:file:bg-primary/20 cursor-pointer"
            />
          </div>

          {/* Pratinjau Data yang Diparsing */}
          {parsedRows.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-xs font-bold text-ink">
                    Pratinjau Data ({parsedRows.length} Baris Siap Diimpor)
                  </span>
                  <p className="text-[11px] text-ink-secondary">
                    Periksa verifikasi presensi dan rata-rata skor sebelum menyimpan ke database.
                  </p>
                </div>
                <span className="text-[11px] text-emerald-600 font-bold bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                  ✓ Format Terverifikasi
                </span>
              </div>

              <div className="max-h-72 overflow-y-auto rounded-lg border border-border">
                <table className="w-full text-[11px] text-left">
                  <thead className="bg-subtle text-ink-secondary border-b border-border font-semibold uppercase tracking-wider sticky top-0">
                    <tr>
                      <th className="px-3 py-2">Kode & Nama</th>
                      <th className="px-3 py-2 text-center">Kehadiran 10 Sesi</th>
                      <th className="px-3 py-2 text-center">Skor Per Sesi</th>
                      <th className="px-3 py-2 text-center">Pre-Test (Rata)</th>
                      <th className="px-3 py-2 text-center">Post-Test (Rata)</th>
                      <th className="px-3 py-2 text-center">Gain</th>
                      <th className="px-3 py-2 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border bg-white">
                    {parsedRows.slice(0, 30).map((r, i) => {
                      const sessionScoresCount = r.sessionScores?.length ?? 0;
                      const gain =
                        r.preTestScore !== null && r.preTestScore !== undefined &&
                        r.postTestScore !== null && r.postTestScore !== undefined
                          ? Math.round((r.postTestScore - r.preTestScore) * 100) / 100
                          : null;

                      return (
                        <tr key={i} className="hover:bg-subtle/40 transition">
                          <td className="px-3 py-2 font-medium">
                            <span className="font-bold text-ink block tabular">{r.code}</span>
                            {r.name && <span className="text-[10px] text-ink-muted">{r.name}</span>}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span className="font-bold tabular text-ink block">{r.attendancePct}%</span>
                            <span className="text-[10px] text-ink-muted tabular">
                              ({r.attendedSessions?.length ?? Math.round((r.attendancePct / 100) * 10)}/10 Sesi)
                            </span>
                          </td>
                          <td className="px-3 py-2 text-center">
                            {sessionScoresCount > 0 ? (
                              <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold bg-primary-soft text-primary">
                                {sessionScoresCount} Sesi Ternilai
                              </span>
                            ) : (
                              <span className="text-ink-muted text-[10px] italic">Global</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center tabular font-medium">
                            {r.preTestScore !== null && r.preTestScore !== undefined ? r.preTestScore : '—'}
                          </td>
                          <td className="px-3 py-2 text-center tabular font-extrabold text-primary">
                            {r.postTestScore !== null && r.postTestScore !== undefined ? r.postTestScore : '—'}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {gain !== null ? (
                              <span
                                className={`inline-block font-extrabold tabular text-[10px] px-1.5 py-0.5 rounded ${
                                  gain > 0
                                    ? 'bg-emerald-100 text-emerald-800'
                                    : gain < 0
                                      ? 'bg-rose-100 text-rose-800'
                                      : 'bg-gray-100 text-gray-700'
                                }`}
                              >
                                {gain > 0 ? `+${gain}` : `${gain}`}
                              </span>
                            ) : (
                              <span className="text-ink-muted">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center uppercase text-[10px] font-bold">
                            <span
                              className={`inline-block px-2 py-0.5 rounded ${
                                r.status === 'lulus'
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : r.status === 'mengikuti'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-amber-100 text-amber-800'
                              }`}
                            >
                              {r.status}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {parsedRows.length > 30 && (
                <p className="text-[10px] text-ink-muted mt-1 italic">
                  ... dan {parsedRows.length - 30} baris lainnya siap diimpor.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-border bg-subtle px-6 py-4">
          <Button variant="secondary" onClick={onClose} disabled={uploading}>
            Batal
          </Button>
          <Button
            onClick={handleImport}
            disabled={parsedRows.length === 0 || uploading}
          >
            {uploading ? 'Mengimpor Data...' : `Impor ${parsedRows.length} Data Sekarang`}
          </Button>
        </div>
      </div>
    </div>
  );
}
